// src/utils/cableShaftUtils.js
import * as THREE from 'three';
import { generateUniqueId } from './generateUniqueId';

/**
 * Kablo Bacası Yardımcı Fonksiyonları
 * - Kablo bağlantı kaydı
 * - Kablo bacası sorgulama
 * - Katlar arası kablo takibi
 */

// Kablo bacası ile nokta arasındaki mesafe eşiği (birim)
const SHAFT_PROXIMITY_THRESHOLD = 50;

/**
 * Verilen noktanın yakınındaki kablo bacasını bulur
 * @param {THREE.Scene} scene - THREE.js sahne
 * @param {Object} point - { x, y } koordinatları
 * @param {number} threshold - Yakınlık eşiği
 * @returns {Object|null} - Bulunan kablo bacası userData veya null
 */
export function findNearestCableShaft(scene, point, threshold = SHAFT_PROXIMITY_THRESHOLD) {
  if (!scene || !point) return null;

  let nearestShaft = null;
  let minDistance = Infinity;

  scene.traverse((obj) => {
    if (obj.userData?.type === 'cableShaft') {
      const center = obj.userData.center;
      if (!center) return;

      const distance = Math.sqrt(
        Math.pow(center.x - point.x, 2) +
        Math.pow(center.y - point.y, 2)
      );

      // Kablo bacasının boyutlarını da hesaba kat
      const halfWidth = (obj.userData.width || 0) / 2;
      const halfHeight = (obj.userData.height || 0) / 2;

      // Nokta, kablo bacasının içinde veya yakınında mı?
      const inBoundsX = point.x >= center.x - halfWidth - threshold &&
                        point.x <= center.x + halfWidth + threshold;
      const inBoundsY = point.y >= center.y - halfHeight - threshold &&
                        point.y <= center.y + halfHeight + threshold;

      if (inBoundsX && inBoundsY && distance < minDistance) {
        minDistance = distance;
        nearestShaft = obj;
      }
    }
  });

  return nearestShaft;
}

/**
 * Bir kabloyu kablo bacasına kaydeder
 * @param {THREE.Scene} scene - THREE.js sahne
 * @param {string} shaftId - Kablo bacası ID
 * @param {Object} cableInfo - Kablo bilgileri
 * @returns {boolean} - Başarılı mı
 */
export function registerCableToShaft(scene, shaftId, cableInfo) {
  if (!scene || !shaftId || !cableInfo) return false;

  let shaft = null;
  scene.traverse((obj) => {
    if (obj.userData?.id === shaftId && obj.userData?.type === 'cableShaft') {
      shaft = obj;
    }
  });

  if (!shaft) {
    console.warn(`[CableShaft] Shaft not found: ${shaftId}`);
    return false;
  }

  // connectedCables dizisini başlat
  if (!shaft.userData.connectedCables) {
    shaft.userData.connectedCables = [];
  }

  // Kablo bilgilerini oluştur
  const cableEntry = {
    wireId: cableInfo.wireId || generateUniqueId(),
    name: cableInfo.name || `Kablo`,
    sourceFloorPlanId: cableInfo.sourceFloorPlanId,
    targetComponent: cableInfo.targetComponent || null,
    targetComponentId: cableInfo.targetComponentId || null,
    direction: cableInfo.direction || 'in', // 'in' (giriş) veya 'out' (çıkış)
    connectionPoint: cableInfo.connectionPoint || shaft.userData.center,
    timestamp: Date.now(),
  };

  // Aynı wireId ile kayıt var mı kontrol et
  const existingIndex = shaft.userData.connectedCables.findIndex(
    c => c.wireId === cableEntry.wireId
  );

  if (existingIndex >= 0) {
    // Güncelle
    shaft.userData.connectedCables[existingIndex] = cableEntry;
  } else {
    // Yeni kayıt
    shaft.userData.connectedCables.push(cableEntry);
  }

  return true;
}

/**
 * Kablo bacasından bir kabloyu kaldırır
 * @param {THREE.Scene} scene - THREE.js sahne
 * @param {string} shaftId - Kablo bacası ID
 * @param {string} wireId - Kaldırılacak kablo ID
 * @returns {boolean} - Başarılı mı
 */
export function unregisterCableFromShaft(scene, shaftId, wireId) {
  if (!scene || !shaftId || !wireId) return false;

  let shaft = null;
  scene.traverse((obj) => {
    if (obj.userData?.id === shaftId && obj.userData?.type === 'cableShaft') {
      shaft = obj;
    }
  });

  if (!shaft || !shaft.userData.connectedCables) return false;

  const initialLength = shaft.userData.connectedCables.length;
  shaft.userData.connectedCables = shaft.userData.connectedCables.filter(
    c => c.wireId !== wireId
  );

  if (shaft.userData.connectedCables.length < initialLength) {
    return true;
  }

  return false;
}

/**
 * Bir kablo bacası grubundaki tüm kabloları getirir
 * @param {THREE.Scene} scene - THREE.js sahne
 * @param {string} shaftGroupId - Kablo bacası grup ID
 * @returns {Array} - Tüm bağlı kablolar
 */
export function getCablesInShaftGroup(scene, shaftGroupId) {
  if (!scene || !shaftGroupId) return [];

  const allCables = [];

  scene.traverse((obj) => {
    if (obj.userData?.type === 'cableShaft' &&
        obj.userData?.shaftGroupId === shaftGroupId) {
      const cables = obj.userData?.connectedCables || [];
      cables.forEach(cable => {
        allCables.push({
          ...cable,
          shaftId: obj.userData.id,
          shaftFloorPlanId: obj.userData.floorPlanId,
        });
      });
    }
  });

  return allCables;
}

/**
 * Belirli bir kattan gelen kabloları filtreler
 * @param {THREE.Scene} scene - THREE.js sahne
 * @param {string} shaftGroupId - Kablo bacası grup ID
 * @param {string} excludeFloorPlanId - Hariç tutulacak kat planı ID
 * @returns {Array} - Diğer katlardan gelen kablolar
 */
export function getCablesFromOtherFloors(scene, shaftGroupId, excludeFloorPlanId) {
  const allCables = getCablesInShaftGroup(scene, shaftGroupId);
  return allCables.filter(c => c.sourceFloorPlanId !== excludeFloorPlanId);
}

/**
 * Wire çizimi bittiğinde kablo bacası bağlantısını kontrol eder
 * @param {THREE.Scene} scene - THREE.js sahne
 * @param {Object} wireData - Wire userData
 * @param {Array} wirePoints - Wire noktaları [{x, y}, ...]
 * @param {string} floorPlanId - Mevcut kat planı ID
 * @returns {Object|null} - Bağlantı bilgisi veya null
 */
export function checkWireShaftConnection(scene, wireData, wirePoints, floorPlanId) {
  if (!scene || !wirePoints || wirePoints.length < 2) return null;

  // Son noktayı kontrol et (wire'ın bittiği yer)
  const endPoint = wirePoints[wirePoints.length - 1];
  const nearestShaft = findNearestCableShaft(scene, endPoint);

  if (nearestShaft) {
    // Kablo bacasına bağlan
    const cableInfo = {
      wireId: wireData?.id || generateUniqueId(),
      name: wireData?.wireName || wireData?.title || 'Kablo',
      sourceFloorPlanId: floorPlanId,
      targetComponent: 'cableShaft',
      targetComponentId: nearestShaft.userData.id,
      direction: 'in',
      connectionPoint: endPoint,
    };

    registerCableToShaft(scene, nearestShaft.userData.id, cableInfo);

    return {
      connected: true,
      shaftId: nearestShaft.userData.id,
      shaftGroupId: nearestShaft.userData.shaftGroupId,
      cableInfo,
    };
  }

  // Başlangıç noktasını da kontrol et
  const startPoint = wirePoints[0];
  const startShaft = findNearestCableShaft(scene, startPoint);

  if (startShaft) {
    const cableInfo = {
      wireId: wireData?.id || generateUniqueId(),
      name: wireData?.wireName || wireData?.title || 'Kablo',
      sourceFloorPlanId: floorPlanId,
      targetComponent: 'cableShaft',
      targetComponentId: startShaft.userData.id,
      direction: 'out', // Bacadan çıkış
      connectionPoint: startPoint,
    };

    registerCableToShaft(scene, startShaft.userData.id, cableInfo);

    return {
      connected: true,
      shaftId: startShaft.userData.id,
      shaftGroupId: startShaft.userData.shaftGroupId,
      cableInfo,
      isStart: true,
    };
  }

  return null;
}

/**
 * Tüm kablo bacalarını getirir
 * @param {THREE.Scene} scene - THREE.js sahne
 * @returns {Array} - Kablo bacaları listesi
 */
export function getAllCableShafts(scene) {
  if (!scene) return [];

  const shafts = [];
  scene.traverse((obj) => {
    if (obj.userData?.type === 'cableShaft') {
      shafts.push({
        id: obj.userData.id,
        shaftGroupId: obj.userData.shaftGroupId,
        floorPlanId: obj.userData.floorPlanId,
        center: obj.userData.center,
        width: obj.userData.width,
        height: obj.userData.height,
        connectedCables: obj.userData.connectedCables || [],
        object: obj,
      });
    }
  });

  return shafts;
}

/**
 * Kablo bacası grubunu ID'ye göre getirir
 * @param {THREE.Scene} scene - THREE.js sahne
 * @param {string} shaftGroupId - Grup ID
 * @returns {Array} - Gruptaki kablo bacaları
 */
export function getShaftsByGroupId(scene, shaftGroupId) {
  if (!scene || !shaftGroupId) return [];

  return getAllCableShafts(scene).filter(s => s.shaftGroupId === shaftGroupId);
}
